import React, { useMemo, useState } from 'react';
import type { PreChatFormConfig, PreChatField } from '../types';

interface PreChatFormProps {
  config: PreChatFormConfig;
  onSubmit: (data: Record<string, string>) => void;
}

const PreChatForm = React.memo(function PreChatForm({ config, onSubmit }: PreChatFormProps) {
  const fields = useMemo(() => config.fields || [], [config.fields]);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach(field => {
      initial[field.id] = '';
    });
    return initial;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: PreChatField, value: string) => {
    setValues(prev => ({ ...prev, [field.id]: value }));
    setErrors(prev => ({ ...prev, [field.id]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    fields.forEach(field => {
      if (field.required && !values[field.id]?.trim()) {
        nextErrors[field.id] = `${field.label} is required.`;
      }
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1 px-4 py-4 space-y-3">
      {config.title && <div className="text-sm font-semibold text-gray-900">{config.title}</div>}
      {config.description && <div className="text-xs text-gray-600">{config.description}</div>}
      {fields.map(field => {
        const fieldId = `prechat-${field.id}`;
        const error = errors[field.id];
        const commonProps = {
          id: fieldId,
          name: field.id,
          value: values[field.id] || '',
          onChange: (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
          ) => handleChange(field, e.target.value),
          placeholder: field.placeholder || '',
          className:
            'w-full rounded border px-3 py-2 text-sm focus:outline-2 focus:outline-blue-500',
        };

        return (
          <div key={field.id} className="space-y-1">
            <label htmlFor={fieldId} className="text-xs font-medium text-gray-700">
              {field.label}
              {field.required ? ' *' : ''}
            </label>
            {field.type === 'textarea' ? (
              <textarea {...commonProps} rows={3} />
            ) : field.type === 'select' ? (
              <select {...commonProps}>
                <option value="">Select an option</option>
                {(field.options || []).map(option => {
                  const value = typeof option === 'string' ? option : option.value;
                  const label = typeof option === 'string' ? option : option.label;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            ) : (
              <input type={field.type || 'text'} {...commonProps} />
            )}
            {error && (
              <div className="text-xs text-red-600" role="alert">
                {error}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="submit"
        className="w-full rounded-lg bg-blue-600 text-white text-sm py-2 hover:opacity-80 focus:outline-2 focus:outline-blue-500"
      >
        {config.submitLabel || 'Start chat'}
      </button>
    </form>
  );
});

export default PreChatForm;
